import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { Rating, Star } from '@smastrom/react-rating';
import '@smastrom/react-rating/style.css';

import './style.scss';

const RATING_ITEMS = 5;
const RATING_ITEM_LABELS = [
  '1 star out of 5',
  '2 stars out of 5',
  '3 stars out of 5',
  '4 stars out of 5',
  '5 stars out of 5',
];

const customStyles = {
  itemShapes: Star,
  itemStrokeWidth: 1,
};

function UserFeedback({
  question,
  submitting = false,
  onSubmit,
  submittedFeedback = true,
  hasSubmitButton = true,
  disableAfterSubmit = false,
  // Opt-in so Giving and Mentorship keep their placeholder-only layout until they choose it too.
  showVisibleLabel = false,
  // Opt-in for the same reason: shows the "N out of 5" value and makes the rating required, which
  // also removes click-to-clear on the selected star.
  accessibleRating = false,
  onRatingChange = () => {},
  onFeedbackChange = () => {},
  idPrefix,
}) {
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(null);
  const wasSubmitted = useRef(submittedFeedback);
  const thanksRef = useRef(null);
  const defaultIdPrefix = useRef(`user-feedback-${Math.random().toString(36).substr(2, 8)}`).current;
  const resolvedIdPrefix = idPrefix || defaultIdPrefix;
  const ratingQuestionId = `${resolvedIdPrefix}-rating-question`;
  const feedbackTextareaId = `${resolvedIdPrefix}-textarea`;

  // This used to write into an sr-only polite region and was regularly not spoken: the same commit
  // that renders the thank-you also unmounts the Submit button, which still has focus, so focus
  // falls to <body> and VoiceOver discards pending polite speech while it re-syncs its cursor.
  // Focusing the message instead is read on focus, which is not subject to that race.
  //
  // The trap: the message only renders when `hasSubmitButton` is true, so a consumer that hides the
  // button AND flips `submittedFeedback` would get silence. Both current such consumers
  // (alumniGiving paymentModal, mentorship ratingModal) pass a constant `submittedFeedback={false}`
  // and never make that transition; one that does needs its own announcement.
  useEffect(() => {
    if (submittedFeedback && !wasSubmitted.current && thanksRef.current) {
      thanksRef.current.focus();
    }
    wasSubmitted.current = submittedFeedback;
  }, [submittedFeedback]);

  useEffect(() => {
    onRatingChange(rating);
  }, [rating]);

  useEffect(() => {
    onFeedbackChange(feedback);
  }, [feedback]);

  const isLocked = disableAfterSubmit && submittedFeedback;

  // The rating's key handler switches on `event.code`, which React 16 synthetic events lack, so every
  // key fell through to its preventDefault branch: Tab could not leave the first star and arrows,
  // Enter and Space did nothing. React hands this same event object to the star's handler next.
  function exposeKeyCode(event) {
    // eslint-disable-next-line no-param-reassign
    event.code = event.nativeEvent.code;
  }

  function getPlaceholder() {
    if (rating <= 3) {
      return question.followUp.negative;
    }
    return question.followUp.positive;
  }
  return (
    <div className="user-feedback-wrapper">
      <p id={ratingQuestionId} className="arc-color-black arc-p arc-font-weight-medium">{question.main}</p>
      <div onKeyDownCapture={exposeKeyCode}>
        <Rating
          className="user-feedback-rating"
          itemStyles={customStyles}
          spaceBetween="medium"
          items={RATING_ITEMS}
          visibleLabelId={ratingQuestionId}
          invisibleItemLabels={RATING_ITEM_LABELS}
          resetLabel="No rating selected"
          isRequired={accessibleRating}
          onChange={(val) => setRating(val)}
          value={rating}
        />
      </div>
      <If condition={accessibleRating && rating}>
        <p className="arc-p rating-value-text">{`${rating} out of ${RATING_ITEMS}`}</p>
      </If>
      <If condition={rating}>
        <div className="feedback-text-wrapper mt16">
          <If condition={showVisibleLabel}>
            <label htmlFor={feedbackTextareaId} className="arc-p arc-color-B55 feedback-label">
              {getPlaceholder() || 'Additional feedback'}
            </label>
          </If>
          <Input.TextArea
            id={feedbackTextareaId}
            rows={6}
            className={`text-content ${submittedFeedback ? 'inactive' : ''}`}
            readOnly={isLocked}
            aria-disabled={isLocked || undefined}
            bordered={false}
            maxLength={512}
            resize={false}
            placeholder={showVisibleLabel ? undefined : getPlaceholder()}
            aria-label={showVisibleLabel ? undefined : getPlaceholder() || 'Additional feedback'}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <If condition={hasSubmitButton}>
            <Choose>
              <When condition={!submittedFeedback || submitting}>
                <Button
                  type="primary"
                  className="light-button mt8"
                  block
                  loading={submitting}
                  onClick={() => onSubmit({ rating, comment: feedback })}>
                  Submit
                </Button>
              </When>
              <Otherwise>
                <p className="mt16 arc-color-success arc-H200" ref={thanksRef} tabIndex={-1}>
                  Thank you for your feedback
                </p>
              </Otherwise>
            </Choose>
          </If>
        </div>
      </If>
    </div>
  );
}

export default UserFeedback;
